import { useEffect, useMemo, useState } from 'react';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import { useJobToggles, useSaveJobToggles } from '../api';

export function JobTogglesTab({ tenantId }: { tenantId: string }) {
  const toast = useToast();
  const togglesQuery = useJobToggles(tenantId);
  const saveMut = useSaveJobToggles(tenantId);

  const items = togglesQuery.data?.items ?? [];
  const [states, setStates] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (togglesQuery.data) {
      setStates(Object.fromEntries(togglesQuery.data.items.map((i) => [i.itemKey, i.enabled])));
    }
  }, [togglesQuery.data]);

  // key -> items that depend on it (mirrors job-toggles.js dependents map).
  const dependents = useMemo(() => {
    const map: Record<string, string[]> = {};
    items.forEach((i) => {
      i.dependsOn.forEach((d) => {
        (map[d] = map[d] ?? []).push(i.itemKey);
      });
    });
    return map;
  }, [items]);

  const depsOf = useMemo(
    () => Object.fromEntries(items.map((i) => [i.itemKey, i.dependsOn])),
    [items],
  );

  function toggle(key: string, checked: boolean) {
    setStates((prev) => {
      const next = { ...prev };
      if (!checked) {
        const blockers = (dependents[key] ?? []).filter((d) => next[d]);
        if (blockers.length) {
          toast.error(`'${key}' can't be turned off — still required by: ${blockers.join(', ')}`);
          return prev;
        }
        next[key] = false;
      } else {
        next[key] = true;
        (depsOf[key] ?? []).forEach((d) => {
          next[d] = true;
        });
      }
      return next;
    });
  }

  function save() {
    saveMut.mutate(states, {
      onSuccess: () => toast.success('Saved. Changes apply within ~2 minutes.'),
      onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed to save toggles.'),
    });
  }

  if (togglesQuery.isLoading) return <Spinner />;
  if (togglesQuery.isError) return <ErrorState onRetry={() => togglesQuery.refetch()} />;

  return (
    <div>
      <div className="alert alert-light border mb-3">
        Turn individual syncs and jobs on or off for this client.
      </div>
      <div className="row">
        {items.map((item) => (
          <div className="col-sm-12 col-md-6 col-lg-4" key={item.itemKey}>
            <div className="form-check form-switch my-2">
              <input
                type="checkbox"
                role="switch"
                className="form-check-input"
                id={`jt-${item.itemKey}`}
                checked={states[item.itemKey] ?? false}
                onChange={(e) => toggle(item.itemKey, e.target.checked)}
              />
              <label className="form-check-label" htmlFor={`jt-${item.itemKey}`}>
                {item.displayName} <small className="text-muted">({item.kind})</small>
              </label>
            </div>
          </div>
        ))}
      </div>
      <div className="d-flex justify-content-end align-items-center">
        <button type="button" className="btn btn-primary ms-auto" onClick={save} disabled={saveMut.isPending}>
          {saveMut.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
